import {
  addToaster,
  GetGenericEntityListingApiType,
  getGenericEntityListingAPI,
  hideLoader,
  showLoader,
  WorkflowInstanceTaskType,
  WORKFLOW_UI_IDENTIFIER,
} from '@app-store'
import {
  Accordion,
  Checkbox,
  ControlledDropDown,
  CustomButton,
  DATE_FIELD_TYPES,
  FormDateInput,
  SkeletonLoader,
  ViewComponent,
} from '@ui-kit'
import {
  NAMESPACES,
  SP_FILL_DETAILS_CONSTANTS,
  SP_FILL_DETAILS_EMPTY_FORM_VALUES,
  SP_FILL_DETAILS_FORM_ID,
  SP_FILL_DETAILS_FORM_SELECT_FIELDS,
  SP_FILL_STATUS_OPTIONS,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import { SP_FILL_DETAILS_STRINGS } from '../../utils/strings'
import { formatLocalDate, getCurrentLocalDate } from '../../utils/date.utils'
import { UpdateSPFillDetailsFormValues } from '../../types/workflow.types'
import { MouseEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Control, FieldValues, useForm } from 'react-hook-form'
import { editEntityData } from '../../apis/entityData.api'
import { useAppDispatch } from '../../utils/agent.store'
import { TOASTER_NAMESPACE } from '../../constants/addEditPage.strings'
import { v4 as uuid } from 'uuid'

export const UpdateSPFillDetails = ({
  task,
}: {
  task: WorkflowInstanceTaskType
}) => {
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState(true)
  const [isEditMode, setIsEditMode] = useState(false)
  const [recordId, setRecordId] = useState<number | null>(null)
  const { caseId: caseIdParam } = useParams()
  const caseId = caseIdParam ?? task?.case_id?.toString()
  const entityName =
    task?.identifier?.entity_name ?? SP_FILL_DETAILS_CONSTANTS.ENTITY_NAME
  const entityId = Number(task?.identifier?.entity_id)
  const layoutId = Number(task?.identifier?.layout_info?.layout_id) || 0
  const isViewMode = !isEditMode
  const isTaskOpen = task?.state === WORKFLOW_STATUS.OPEN

  const {
    setValue,
    register,
    reset,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<UpdateSPFillDetailsFormValues>({
    defaultValues: SP_FILL_DETAILS_EMPTY_FORM_VALUES,
  })

  const {
    sp_fill_status: fillStatus,
    shipment_date: shipmentDate,
    next_followup_duedate: followUpDate,
    supp_act_contact_patient: contactPatient,
    supp_act_contact_provider: contactProvider,
    supp_act_contact_field_team: contactFieldTeam,
  } = watch()
  const isPendingStatus =
    fillStatus === SP_FILL_DETAILS_CONSTANTS.FILL_STATUS_PENDING
  const isShippedStatus =
    fillStatus === SP_FILL_DETAILS_CONSTANTS.FILL_STATUS_SHIPPED
  const isUnableToShipStatus =
    fillStatus === SP_FILL_DETAILS_CONSTANTS.FILL_STATUS_UNABLE_TO_SHIP
  const hasFollowUpDate = Boolean(followUpDate)
  const hasSupportActivity = Boolean(
    contactPatient || contactProvider || contactFieldTeam
  )
  const isShipmentDateDisabled = !isShippedStatus
  const isFollowUpDisabled = !isPendingStatus || hasSupportActivity
  const isSupportActivityDisabled =
    isShippedStatus || (isPendingStatus && hasFollowUpDate)
  const isContactPatientOrProviderDisabled =
    isSupportActivityDisabled || isUnableToShipStatus

  const handleStatusChange = (status: string) => {
    const values: UpdateSPFillDetailsFormValues = {
      ...SP_FILL_DETAILS_EMPTY_FORM_VALUES,
      sp_fill_status: status,
    }

    if (status === SP_FILL_DETAILS_CONSTANTS.FILL_STATUS_UNABLE_TO_SHIP) {
      values.supp_act_contact_patient = true
      values.supp_act_contact_provider = true
    }

    reset(values)
  }

  const fetchSPFillDetails = async () => {
    if (!caseId) {
      setRecordId(null)
      reset(SP_FILL_DETAILS_EMPTY_FORM_VALUES)
      setIsLoading(false)
      return
    }

    try {
      const response = await getGenericEntityListingAPI({
        entity_name: entityName,
        select: SP_FILL_DETAILS_FORM_SELECT_FIELDS,
        distinct: 'true',
        page: 0,
        size: 25,
        filters: [
          {
            criteria: [
              {
                field_name: SP_FILL_DETAILS_CONSTANTS.CASES_ID,
                operation: 'EQUALS',
                values: [caseId],
              },
            ],
          },
        ],
      } as unknown as GetGenericEntityListingApiType)
      const record = response?.content?.filter(
        (rec: FieldValues) => rec?.id == task?.resource_id
      )
      const recordExists = Boolean(record?.[0])
      setRecordId(record?.[0]?.id ?? null)
      reset(
        recordExists
          ? { ...SP_FILL_DETAILS_EMPTY_FORM_VALUES, ...record?.[0] }
          : SP_FILL_DETAILS_EMPTY_FORM_VALUES
      )
    } catch (err) {
      console.error(err)
      dispatch(
        addToaster({
          id: uuid(),
          type: 'error',
          message: NAMESPACES.SOMETHING_WENT_WRONG,
        })
      )
      setRecordId(null)
      reset(SP_FILL_DETAILS_EMPTY_FORM_VALUES)
    } finally {
      setIsLoading(false)
    }
  }

  const handleEditClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsEditMode(true)
  }

  const onSubmit = async (data: UpdateSPFillDetailsFormValues) => {
    if (!entityId || !recordId) {
      dispatch(
        addToaster({
          id: uuid(),
          type: 'error',
          message: NAMESPACES.SOMETHING_WENT_WRONG,
        })
      )
      return
    }

    try {
      dispatch(showLoader())
      const response = await editEntityData(
        entityName,
        entityId,
        layoutId,
        {
          ...data,
          id: recordId,
        },
        recordId
      )
      if (response) {
        dispatch(
          addToaster({
            id: uuid(),
            type: 'success',
            message: TOASTER_NAMESPACE.SAVE_SUCCESS,
          })
        )
        setIsEditMode(false)
        setIsLoading(true)
        await fetchSPFillDetails()
      }
    } catch (err) {
      console.error(err)
      dispatch(
        addToaster({
          id: uuid(),
          type: 'error',
          message: TOASTER_NAMESPACE.FAILED_TO_EDIT_RECORD,
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }

  useEffect(() => {
    fetchSPFillDetails()
  }, [])

  useEffect(() => {
    if (!isTaskOpen) {
      setIsEditMode(false)
    }
  }, [isTaskOpen])

  if (isLoading) {
    return (
      <div className="p-4">
        <SkeletonLoader columns={3} />
      </div>
    )
  }

  return (
    <div className="p-4">
      <form
        id={SP_FILL_DETAILS_FORM_ID}
        onSubmit={(e) => {
          e.preventDefault()
        }}
      >
        {isTaskOpen && (
          <div className="flex justify-end pb-2">
            <CustomButton
              type="button"
              isFilled={isEditMode}
              dataQaId={isEditMode ? 'save-button' : 'edit-button'}
              onClick={isEditMode ? handleSubmit(onSubmit) : handleEditClick}
            >
              {isEditMode ? NAMESPACES.SAVE_BUTTON : NAMESPACES.EDIT_BUTTON}
            </CustomButton>
          </div>
        )}
        <div className="border rounded-b border-accent-100 flex flex-col">
          <Accordion
            heading={
              <span className="flex items-center">
                {SP_FILL_DETAILS_STRINGS?.UPDATE_SP_FILL_DETAILS}
              </span>
            }
            alwaysOpen={true}
          >
            <div className="p-4 flex flex-col gap-4 w-full">
              {isViewMode ? (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <ViewComponent
                      label={SP_FILL_DETAILS_CONSTANTS.SP_FILL_STATUS_LABEL}
                      data={fillStatus || '--'}
                    />
                    <ViewComponent
                      label={SP_FILL_DETAILS_CONSTANTS.SHIPMENT_DATE_LABEL}
                      data={
                        shipmentDate
                          ? formatLocalDate(shipmentDate, 'MM/DD/YYYY', false)
                          : '--'
                      }
                    />
                    {task?.identifier?.ui_identifier ===
                    WORKFLOW_UI_IDENTIFIER?.SP_FILL_STATUS_RECORD_FOLLOW_UP ? (
                      <ViewComponent
                        label={
                          SP_FILL_DETAILS_CONSTANTS.NEXT_FOLLOWUP_DUEDATE_LABEL
                        }
                        data={
                          followUpDate
                            ? formatLocalDate(followUpDate, 'MM/DD/YYYY', false)
                            : '--'
                        }
                      />
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-3">
                    <span className="font-primary text-sm font-medium">
                      {SP_FILL_DETAILS_CONSTANTS.SUPPORT_ACTIVITIES_HEADING}
                    </span>
                    <div className="grid grid-cols-3 gap-4">
                      <ViewComponent
                        label={
                          SP_FILL_DETAILS_CONSTANTS.SUPP_ACT_CONTACT_PATIENT_LABEL
                        }
                        data={contactPatient ? 'Yes' : 'No'}
                      />
                      <ViewComponent
                        label={
                          SP_FILL_DETAILS_CONSTANTS.SUPP_ACT_CONTACT_PROVIDER_LABEL
                        }
                        data={contactProvider ? 'Yes' : 'No'}
                      />
                      <ViewComponent
                        label={
                          SP_FILL_DETAILS_CONSTANTS.SUPP_ACT_CONTACT_FIELD_TEAM_LABEL
                        }
                        data={contactFieldTeam ? 'Yes' : 'No'}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <ControlledDropDown
                      control={control}
                      name="sp_fill_status"
                      label={SP_FILL_DETAILS_CONSTANTS.SP_FILL_STATUS_LABEL}
                      options={SP_FILL_STATUS_OPTIONS}
                      required={true}
                      errors={errors}
                      onChange={(option) => {
                        handleStatusChange(option?.value ?? '')
                      }}
                    />
                    <FormDateInput
                      control={control as unknown as Control<FieldValues>}
                      name="shipment_date"
                      label={SP_FILL_DETAILS_CONSTANTS.SHIPMENT_DATE_LABEL}
                      type={DATE_FIELD_TYPES.DATE_TYPE}
                      min=""
                      max=""
                      isDisabled={isShipmentDateDisabled}
                      required={!isShipmentDateDisabled}
                      handleChange={(date?: string | null) => {
                        setValue('shipment_date', date ?? null)
                      }}
                    />
                    {task?.identifier?.ui_identifier ===
                    WORKFLOW_UI_IDENTIFIER?.SP_FILL_STATUS_RECORD_FOLLOW_UP ? (
                      <FormDateInput
                        control={control as unknown as Control<FieldValues>}
                        name="next_followup_duedate"
                        label={
                          SP_FILL_DETAILS_CONSTANTS.NEXT_FOLLOWUP_DUEDATE_LABEL
                        }
                        type={DATE_FIELD_TYPES.DATE_TYPE}
                        min={getCurrentLocalDate('YYYY-MM-DD', false)}
                        max=""
                        showClear={true}
                        isDisabled={isFollowUpDisabled}
                        handleChange={(date?: string | null) => {
                          setValue('next_followup_duedate', date ?? null)
                        }}
                      />
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-3">
                    <span className="font-primary text-sm font-medium">
                      {SP_FILL_DETAILS_CONSTANTS.SUPPORT_ACTIVITIES_HEADING}
                    </span>
                    <div className="flex flex-wrap gap-6">
                      <Checkbox
                        name="supp_act_contact_patient"
                        label={
                          SP_FILL_DETAILS_CONSTANTS.SUPP_ACT_CONTACT_PATIENT_LABEL
                        }
                        register={register}
                        disabled={isContactPatientOrProviderDisabled}
                      />
                      <Checkbox
                        name="supp_act_contact_provider"
                        label={
                          SP_FILL_DETAILS_CONSTANTS.SUPP_ACT_CONTACT_PROVIDER_LABEL
                        }
                        register={register}
                        disabled={isContactPatientOrProviderDisabled}
                      />
                      <Checkbox
                        name="supp_act_contact_field_team"
                        label={
                          SP_FILL_DETAILS_CONSTANTS.SUPP_ACT_CONTACT_FIELD_TEAM_LABEL
                        }
                        register={register}
                        disabled={isSupportActivityDisabled}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </Accordion>
        </div>
      </form>
    </div>
  )
}
